'use client';
import { useMemo, useState, useRef } from 'react';
import Link from '../components/SafeLink';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const CustomCheckbox = ({ name, isChecked, onChange, link }: { name: string, isChecked: boolean, onChange: (e: any) => void, link: string }) => (
  <div className="flex items-start space-x-2">
    <input
      type="checkbox"
      id={name}
      name={name}
      checked={isChecked}
      onChange={onChange}
      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
    />
    <label htmlFor={name} className="text-sm text-[#475569]">
      Я даю согласие на <Link href={link} className="text-blue-600 underline hover:no-underline">обработку моих персональных данных</Link>.
    </label>
  </div>
);

function num(n: number, digits = 2) {
  return n.toLocaleString("ru-RU", { maximumFractionDigits: digits });
}

function computeFTE(
  demandUnits: number,
  minutesPerUnit: number,
  minutesPerFTE: number,
  upliftPct: number,
  roundUp: boolean
) {
  const totalMinutes = Math.max(0, demandUnits * minutesPerUnit);
  const baseFTEraw = minutesPerFTE > 0 ? totalMinutes / minutesPerFTE : 0;
  const postFTEraw = baseFTEraw * (1 - Math.max(0, Math.min(100, upliftPct)) / 100);
  const baseFTE = roundUp ? Math.ceil(baseFTEraw) : baseFTEraw;
  const postFTE = roundUp ? Math.ceil(postFTEraw) : postFTEraw;
  const savedFTE = Math.max(0, baseFTE - postFTE);
  return { baseFTE, postFTE, savedFTE };
}

function SimpleFTECalculator() {
  const [isExpert, setIsExpert] = useState(false);
  const [demandUnits, setDemandUnits] = useState<number>(100000);
  const [timeUnitValue, setTimeUnitValue] = useState<number>(40);
  const [timeUnitIsHours, setTimeUnitIsHours] = useState<boolean>(false);
  const [minutesPerFTE, setMinutesPerFTE] = useState<number>(9500);
  const [upliftPct, setUpliftPct] = useState<number>(12);
  const [roundUp, setRoundUp] = useState<boolean>(true);
  const [rateRUB, setRateRUB] = useState<number>(400);
  const [otShareNowPct, setOtShareNowPct] = useState<number>(25);
  const [otReductionPct, setOtReductionPct] = useState<number>(25);
  const [otMultiplier, setOtMultiplier] = useState<number>(1.5);
  type Scenario = {
    name: string;
    baseFTE: number; postFTE: number; savedFTE: number;
    totalPayrollBase: number; totalPayrollPost: number; payrollSavingsMonthly: number;
  };
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const reportRef = useRef<HTMLDivElement>(null);

  const presets = [
    { label: "Склад / логистика", demand: 100000, timePerUnitMin: 40, minutesFTE: 9500, uplift: 12, otShare: 25, rate: 420 },
    { label: "Ритейл / касса", demand: 180000, timePerUnitMin: 7, minutesFTE: 10080, uplift: 10, otShare: 20, rate: 380 },
    { label: "Производство (лёгкое)", demand: 80000, timePerUnitMin: 12, minutesFTE: 10080, uplift: 15, otShare: 22, rate: 450 },
    { label: "HoReCa / кухня", demand: 120000, timePerUnitMin: 8, minutesFTE: 9500, uplift: 12, otShare: 30, rate: 360 },
  ];
  const applyPreset = (p: (typeof presets)[number]) => {
    setDemandUnits(p.demand);
    setTimeUnitIsHours(false);
    setTimeUnitValue(p.timePerUnitMin);
    setMinutesPerFTE(p.minutesFTE);
    setUpliftPct(p.uplift);
    setOtShareNowPct(p.otShare);
    setRateRUB(p.rate);
  };

  const minutesPerUnit = useMemo(() => (timeUnitIsHours ? timeUnitValue * 60 : timeUnitValue), [timeUnitIsHours, timeUnitValue]);
  const totalMinutes = useMemo(() => Math.max(0, demandUnits * minutesPerUnit), [demandUnits, minutesPerUnit]);
  const baseFTEraw = useMemo(() => (minutesPerFTE > 0 ? totalMinutes / minutesPerFTE : 0), [totalMinutes, minutesPerFTE]);
  const postFTEraw = useMemo(() => baseFTEraw * (1 - Math.max(0, Math.min(100, upliftPct)) / 100), [baseFTEraw, upliftPct]);
  const baseFTE = roundUp ? Math.ceil(baseFTEraw) : baseFTEraw;
  const postFTE = roundUp ? Math.ceil(postFTEraw) : postFTEraw;
  const savedFTE = Math.max(0, baseFTE - postFTE);
  const hoursPerFTE = useMemo(() => minutesPerFTE / 60, [minutesPerFTE]);

  const regularPayrollBase = useMemo(() => baseFTE * hoursPerFTE * rateRUB, [baseFTE, hoursPerFTE, rateRUB]);
  const otShareNow = Math.max(0, Math.min(1, otShareNowPct / 100));
  const totalPayrollBase = useMemo(() => (1 - otShareNow > 0 ? regularPayrollBase / (1 - otShareNow) : regularPayrollBase), [regularPayrollBase, otShareNow]);
  const overtimePayrollBase = Math.max(0, totalPayrollBase - regularPayrollBase);
  const overtimeHoursBase = rateRUB * otMultiplier > 0 ? overtimePayrollBase / (rateRUB * otMultiplier) : 0;

  const postOTshare = Math.max(0, otShareNow * (1 - Math.max(0, Math.min(100, otReductionPct)) / 100));
  const regularPayrollPost = useMemo(() => postFTE * hoursPerFTE * rateRUB, [postFTE, hoursPerFTE, rateRUB]);
  const totalPayrollPost = useMemo(() => (1 - postOTshare > 0 ? regularPayrollPost / (1 - postOTshare) : regularPayrollPost), [regularPayrollPost, postOTshare]);
  const overtimePayrollPost = Math.max(0, totalPayrollPost - regularPayrollPost);
  const overtimeHoursPost = rateRUB * otMultiplier > 0 ? overtimePayrollPost / (rateRUB * otMultiplier) : 0;

  const payrollSavingsMonthly = Math.max(0, totalPayrollBase - totalPayrollPost);
  const payrollSavingsYear = payrollSavingsMonthly * 12;
  const overtimeSavingsMonthly = Math.max(0, overtimePayrollBase - overtimePayrollPost);
  const overtimeHoursSaved = Math.max(0, overtimeHoursBase - overtimeHoursPost);

  const chartFTE = [{ name: "До", FTE: Number(baseFTE.toFixed(2)) }, { name: "После", FTE: Number(postFTE.toFixed(2)) }];
  const chartPayroll = [{ name: "До", ФОТ: Number(totalPayrollBase.toFixed(0)) }, { name: "После", ФОТ: Number(totalPayrollPost.toFixed(0)) }];

  const addScenario = () => {
    if (scenarios.length >= 3) return alert("Максимум 3 сценария");
    const name = prompt("Название сценария:", `Сценарий ${scenarios.length + 1}`);
    if (!name) return;
    setScenarios([...scenarios, { name, baseFTE, postFTE, savedFTE, totalPayrollBase, totalPayrollPost, payrollSavingsMonthly }]);
  };
  const clearScenarios = () => setScenarios([]);

  const compareFTE = scenarios.map(s => ({ name: s.name, "FTE (база)": Number(s.baseFTE.toFixed(2)), "FTE (после)": Number(s.postFTE.toFixed(2)) }));
  const comparePayroll = scenarios.map(s => ({ name: s.name, "ФОТ (база)": Math.round(s.totalPayrollBase), "ФОТ (после)": Math.round(s.totalPayrollPost) }));

  async function exportXLSX() {
    const XLSX: any = await import("xlsx");
    const wb = XLSX.utils.book_new();
    const inputs = [
      ["Параметр", "Значение"], ["Спрос/месяц (ед.)", demandUnits],
      ["Время на 1 ед. (мин)", minutesPerUnit], ["Минуты 1 FTE/мес", minutesPerFTE],
      ["Прирост продуктивности, %", upliftPct], ["Ставка, ₽/час", rateRUB],
      ["OT сейчас, % ФОТ", otShareNowPct], ["Снижение OT, %", otReductionPct],
      ["Коэфф. OT", otMultiplier],
    ];
    const outputs = [
      ["Показатель", "Значение"], ["FTE (база)", baseFTE], ["FTE (после)", postFTE],
      ["Экономия, ЭПЗ", savedFTE], ["ФОТ (база), ₽/мес", Math.round(totalPayrollBase)],
      ["ФОТ (после), ₽/мес", Math.round(totalPayrollPost)], ["Экономия ФОТ, ₽/мес", Math.round(payrollSavingsMonthly)],
      ["Экономия ФОТ, ₽/год", Math.round(payrollSavingsYear)],
      ["Overtime (база), ₽/мес", Math.round(overtimePayrollBase)], ["Overtime (после), ₽/мес", Math.round(overtimePayrollPost)],
      ["Экономия Overtime, ₽/мес", Math.round(overtimeSavingsMonthly)],
      ["Overtime часы (база), ч/мес", Number(overtimeHoursBase.toFixed(1))],
      ["Overtime часы (после), ч/мес", Number(overtimeHoursPost.toFixed(1))],
      ["Экономия Overtime, ч/мес", Number(overtimeHoursSaved.toFixed(1))],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inputs), "Входные");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(outputs), "Результат");
    if (scenarios.length) {
      const wsSc = XLSX.utils.aoa_to_sheet([
        ["Сценарий", "FTE (база)", "FTE (после)", "Экономия FTE", "ФОТ (база)", "ФОТ (после)", "Экономия ФОТ/мес"],
        ...scenarios.map(s => [s.name, s.baseFTE, s.postFTE, s.savedFTE, Math.round(s.totalPayrollBase), Math.round(s.totalPayrollPost), Math.round(s.payrollSavingsMonthly)])
      ]);
      XLSX.utils.book_append_sheet(wb, wsSc, "Сценарии");
    }
    XLSX.writeFile(wb, "interim_fte_calculator.xlsx");
  }

  async function exportPDF() {
    const html2canvas: any = (await import("html2canvas")).default;
    const jsPDF: any = (await import("jspdf")).default;
    if (!reportRef.current) return;
    const canvas = await html2canvas(reportRef.current, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.text("INTERIM — Калькулятор ЭПЗ", 40, 32);
    pdf.addImage(imgData, "PNG", 20, 40, imgWidth, Math.min(imgHeight, pdf.internal.pageSize.getHeight() - 60));
    pdf.save("interim_fte_report.pdf");
  }

  const tests = [
    {
      name: "Кейс 1 — из описания (округление вверх)",
      input: { demand: 100000, minPerUnit: 40, minPerFTE: 9500, uplift: 12, roundUp: true },
      expected: { base: 422, post: 371, saved: 51 }, 
    },
    {
      name: "Кейс 2 — ритейл (ровно кратно)",
      input: { demand: 180000, minPerUnit: 7, minPerFTE: 10080, uplift: 10, roundUp: true },
      expected: { base: 125, post: 113, saved: 12 }, 
    },
    {
      name: "Кейс 3 — время в часах (0.5 ч = 30 мин)",
      input: { demand: 1000, minPerUnit: 30, minPerFTE: 10080, uplift: 0, roundUp: true },
      expected: { base: 3, post: 3, saved: 0 }, 
    },
  ];
  const testResults = tests.map((t) => {
    const { baseFTE, postFTE, savedFTE } = computeFTE(
      t.input.demand,
      t.input.minPerUnit,
      t.input.minPerFTE,
      t.input.uplift,
      t.input.roundUp
    );
    const pass = baseFTE === t.expected.base && postFTE === t.expected.post && savedFTE === t.expected.saved;
    return { ...t, got: { base: baseFTE, post: postFTE, saved: savedFTE }, pass };
  });

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6 bg-white rounded-2xl shadow-[0_10px_28px_rgba(2,6,23,.08)]" ref={reportRef}>
      <header className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Калькулятор ЭПЗ с экономией ФОТ</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Режим:</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => setIsExpert(false)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${!isExpert ? "bg-blue-50 border-blue-400" : "hover:bg-gray-50"}`}>
                Руководитель
              </button>
              <button type="button" onClick={() => setIsExpert(true)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${isExpert ? "bg-blue-50 border-blue-400" : "hover:bg-gray-50"}`}>
                Эксперт
              </button>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600">Заполните 3–4 поля, либо выберите пресет.  В режиме «Эксперт» доступны параметры ФОТ/переработок и сравнение сценариев.</p>
      </header>

      <section className="bg-white rounded-2xl shadow p-4 space-y-3">
        <div className="text-sm font-semibold">Пресеты (редактируемые):</div>
        <div className="flex flex-wrap gap-2">
          {presets.map(p => (
            <button key={p.label} type="button" onClick={() => applyPreset(p)}
              className="px-3 py-2 rounded-xl border hover:bg-gray-50 text-sm">
              {p.label}
            </button>
          ))}
        </div>
      </section>

      <section className="grid xl:grid-cols-12 gap-4">
        <div className="xl:col-span-4 bg-white rounded-2xl shadow p-4 space-y-4">
          <h2 className="font-semibold">Входные данные</h2>
          <label className="block text-sm font-medium">Спрос/месяц (ед.)</label>
          <input type="number" className="w-full rounded-xl border p-2" value={demandUnits}
            onChange={e => setDemandUnits(Math.max(0, Number(e.target.value)))} />
          <div className="grid grid-cols-5 gap-2 items-end">
            <div className="col-span-3">
              <label className="block text-sm font-medium">Время на 1 ед.</label>
              <input type="number" step="0.1" className="w-full rounded-xl border p-2" value={timeUnitValue}
                onChange={e => setTimeUnitValue(Math.max(0, Number(e.target.value)))} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium">Ед.</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setTimeUnitIsHours(false)}
                  className={`flex-1 rounded-xl border p-2 text-sm ${!timeUnitIsHours ? "bg-blue-50 border-blue-400" : ""}`}>мин</button>
                <button type="button" onClick={() => setTimeUnitIsHours(true)}
                  className={`flex-1 rounded-xl border p-2 text-sm ${timeUnitIsHours ? "bg-blue-50 border-blue-400" : ""}`}>час</button>
              </div>
            </div>
          </div>
          <label className="block text-sm font-medium">Минуты 1 ЭПЗ в месяц</label>
          <input type="number" className="w-full rounded-xl border p-2" value={minutesPerFTE}
            onChange={e => setMinutesPerFTE(Math.max(1, Number(e.target.value)))} />
          <div className="flex items-center gap-2">
            <input id="roundUp" type="checkbox" className="h-4 w-4" checked={roundUp}
              onChange={e => setRoundUp(e.target.checked)} />
            <label htmlFor="roundUp" className="text-sm">Округлять до целых ставок</label>
          </div>
        </div>

        <div className="xl:col-span-4 bg-white rounded-2xl shadow p-4 space-y-4">
          <h2 className="font-semibold">Оптимизация</h2>
          <label className="block text-sm font-medium">Прирост продуктивности (%)</label>
          <input type="number" step="0.5" className="w-full rounded-xl border p-2" value={upliftPct}
            onChange={e => setUpliftPct(Math.max(0, Math.min(100, Number(e.target.value))))} />
          {isExpert && (
            <>
              <label className="block text-sm font-medium">OT сейчас (% ФОТ)</label>
              <input type="number" step="0.5" className="w-full rounded-xl border p-2" value={otShareNowPct}
                onChange={e => setOtShareNowPct(Math.max(0, Math.min(95, Number(e.target.value))))} />
              <label className="block text-sm font-medium">Снижение переработок (%)</label>
              <input type="number" className="w-full rounded-xl border p-2" value={otReductionPct}
                onChange={e => setOtReductionPct(Math.max(0, Math.min(100, Number(e.target.value))))} />
              <label className="block text-sm font-medium">Коэфф. оплаты переработок (×)</label>
              <input type="number" step="0.1" className="w-full rounded-xl border p-2" value={otMultiplier}
                onChange={e => setOtMultiplier(Math.max(1, Number(e.target.value)))} />
              <label className="block text-sm font-medium">Ставка, ₽/час</label>
              <input type="number" step="10" className="w-full rounded-xl border p-2" value={rateRUB}
                onChange={e => setRateRUB(Math.max(0, Number(e.target.value)))} />
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
                <p className="font-medium mb-1">Подсказка</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Если нет точных данных по переработкам — поставьте 20–30% и варьируйте.</li>
                  <li>Ставка — средняя с налогами/надбавками.</li>
                  <li> Результаты ориентировочные; для точного плана нужен аудит.</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="xl:col-span-4 bg-white rounded-2xl shadow p-4 space-y-4">
          <h2 className="font-semibold">Результаты</h2>
          <div className="grid grid-cols-1 gap-3">
            <div className="rounded-2xl border p-3">
              <div className="text-xs text-gray-500">Базовая потребность, ЭПЗ</div>
              <div className="text-3xl font-bold">{num(baseFTE)}</div>
            </div>
            <div className="rounded-2xl border p-3">
              <div className="text-xs text-gray-500">После внедрения, ЭПЗ</div>
              <div className="text-3xl font-bold">{num(postFTE)}</div>
            </div>
            <div className="rounded-2xl border p-3 bg-emerald-50 border-emerald-200">
              <div className="text-xs text-emerald-700">Экономия, ЭПЗ</div>
              <div className="text-3xl font-bold text-emerald-800">{num(savedFTE)}</div>
            </div>
            <div className="rounded-2xl border p-3">
              <div className="text-xs text-gray-500">ФОТ (база), ₽/мес</div>
              <div className="text-2xl font-semibold">{num(totalPayrollBase, 0)}</div>
            </div>
            <div className="rounded-2xl border p-3">
              <div className="text-xs text-gray-500">ФОТ (после), ₽/мес</div>
              <div className="text-2xl font-semibold">{num(totalPayrollPost, 0)}</div>
            </div>
            <div className="rounded-2xl border p-3 bg-emerald-50 border-emerald-200">
              <div className="text-xs text-emerald-700">Экономия ФОТ</div>
              <div className="text-xl font-bold text-emerald-800">{num(payrollSavingsMonthly, 0)} ₽/мес</div>
              <div className="text-xs text-emerald-700">≈ {num(payrollSavingsYear, 0)} ₽/год</div>
            </div>
            {isExpert && (
              <div className="rounded-2xl border p-3 bg-violet-50 border-violet-200">
                <div className="text-xs text-violet-700">Сверхурочная работа</div>
                <div className="text-sm text-violet-900">
                  База: {num(overtimePayrollBase, 0)} ₽ ({num(overtimeHoursBase, 1)} ч)<br />
                  После: {num(overtimePayrollPost, 0)} ₽ ({num(overtimeHoursPost, 1)} ч)<br />
                  Экономия: {num(overtimeSavingsMonthly, 0)} ₽ и {num(overtimeHoursSaved, 1)} ч/мес
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={exportXLSX} className="px-3 py-2 rounded-xl border hover:bg-gray-50 text-sm">Экспорт XLSX</button>
            <button onClick={exportPDF} className="px-3 py-2 rounded-xl border hover:bg-gray-50 text-sm">Экспорт PDF</button>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow p-4">
          <h3 className="font-semibold mb-2">ЭПЗ(FTE): до vs после</h3>
          <div className="h-64"><ResponsiveContainer><BarChart data={chartFTE}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip formatter={(v: any) => [v, "FTE"]} /><Legend /><Bar dataKey="FTE" /></BarChart></ResponsiveContainer></div>
        </div>
        <div className="bg-white rounded-2xl shadow p-4">
          <h3 className="font-semibold mb-2">ФОТ: до vs после</h3>
          <div className="h-64"><ResponsiveContainer><BarChart data={chartPayroll}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip formatter={(v: any) => [v.toLocaleString("ru-RU"), "₽/мес"]} /><Legend /><Bar dataKey="ФОТ" /></BarChart></ResponsiveContainer></div>
        </div>
      </section>

      {isExpert && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Сравнение сценариев</h3>
            <div className="flex gap-2">
              <button onClick={addScenario} className="px-3 py-2 rounded-xl border hover:bg-gray-50 text-sm">Добавить сценарий</button>
              <button onClick={clearScenarios} className="px-3 py-2 rounded-xl border hover:bg-gray-50 text-sm">Очистить</button>
            </div>
          </div>
          {scenarios.length === 0 ? (
            <p className="text-sm text-gray-600">Нажмите «Добавить», чтобы зафиксировать текущий расчёт.</p>
          ) : (
            <>
              <div className="overflow-x-auto bg-white rounded-2xl shadow">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left p-3">Сценарий</th>
                    <th className="text-right p-3">FTE (база)</th>
                    <th className="text-right p-3">FTE (после)</th>
                    <th className="text-right p-3">Экономия</th>
                    <th className="text-right p-3">ФОТ (база)</th>
                    <th className="text-right p-3">ФОТ (после)</th>
                    <th className="text-right p-3">Экономия ₽/мес</th>
                  </tr></thead>
                  <tbody>
                    {scenarios.map(s => (
                      <tr key={s.name} className="border-t">
                        <td className="p-3">{s.name}</td>
                        <td className="p-3 text-right">{num(s.baseFTE)}</td>
                        <td className="p-3 text-right">{num(s.postFTE)}</td>
                        <td className="p-3 text-right">{num(s.savedFTE)}</td>
                        <td className="p-3 text-right">{num(s.totalPayrollBase, 0)}</td>
                        <td className="p-3 text-right">{num(s.totalPayrollPost, 0)}</td>
                        <td className="p-3 text-right">{num(s.payrollSavingsMonthly, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      <section className="rounded-2xl border p-4 bg-white">
        <h3 className="font-semibold mb-2">Как интерпретировать</h3>
        <ol className="list-decimal pl-5 space-y-1 text-sm">
          <li>Если «Экономия, ЭПЗ(Эквивалент полной занятости)» 0 — гасим переработки, переносим людей на узкие места, замораживаем найм.</li>
          <li>Если ФОТ(Фонд оплаты труда) почти не меняется — проверьте переработки и ставку: часто занижены.</li>
          <li>Сравнивайте площадки одинаковой методикой — увидите, где потенциал больше.</li>
        </ol>
      </section>

      <section className="rounded-2xl border p-4 bg-white">
        <h3 className="font-semibold mb-2">DEV • Самопроверка (юнит‑тесты)</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2">Название</th>
                <th className="text-right p-2">Ожид. Base</th>
                <th className="text-right p-2">Факт Base</th>
                <th className="text-right p-2">Ожид. Post</th>
                <th className="text-right p-2">Факт Post</th>
                <th className="text-right p-2">Ожид. Saved</th>
                <th className="text-right p-2">Факт Saved</th>
                <th className="text-left p-2">Статус</th>
              </tr>
            </thead>
            <tbody>
              {testResults.map((t) => (
                <tr key={t.name} className="border-t">
                  <td className="p-2">{t.name}</td>
                  <td className="p-2 text-right">{t.expected.base}</td>
                  <td className="p-2 text-right">{t.got.base}</td>
                  <td className="p-2 text-right">{t.expected.post}</td>
                  <td className="p-2 text-right">{t.got.post}</td>
                  <td className="p-2 text-right">{t.expected.saved}</td>
                  <td className="p-2 text-right">{t.got.saved}</td>
                  <td className={`p-2 font-medium ${t.pass ? "text-emerald-700" : "text-red-700"}`}>{t.pass ? "✅ PASS" : "❌ FAIL"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default function EfficiencyPage() {
  const reportRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    consent: false,
  });
  const [formStatus, setFormStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.consent) return setFormStatus('Пожалуйста, дайте согласие на обработку Персональных данных');
    if (!formData.name || !formData.phone || !formData.email) return setFormStatus('⚠️ Заполните все поля');
    setFormStatus('Отправка...');
    await new Promise(r => setTimeout(r, 1000));
    setFormStatus('✅ Заявка отправлена! Скоро свяжемся.');
    setFormData({ name: '', phone: '', email: '', consent: false });
  };

  return (
    <main className="min-h-screen bg-white text-[#0f172a] font-[Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif] pt-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Оптимизация трудовых ресурсов",
          "provider": { "@type": "Organization", "name": "INTERIM" },
          "areaServed": "RU",
          "serviceType": "Workforce Management, Multiskill, Shift Scheduling",
          "termsOfService": "https://www.interim.ru/terms",
        })
      }} />

      <section className="relative flex flex-col lg:flex-row items-center min-h-[58vh] text-white overflow-hidden"
        style={{ background: `radial-gradient(900px 380px at 15% -10%, #2e5ec5 0%, rgba(46,94,197,.6) 30%, transparent 60%), radial-gradient(1200px 500px at 90% 10%, #0b1f56 0%, #071b42 60%, #05142c 100%)` }}>
        <div className="flex-1 px-[48px] py-[64px]">
          <div className="max-w-[1200px] mx-auto px-4">
            <span className="inline-flex items-center gap-2 bg-[rgba(255,255,255,.12)] border border-[rgba(255,255,255,.35)] text-white px-[10px] py-[6px] rounded-full text-[12px]">
              Направление: эффективность
            </span>
            <h1 className="text-[44px] leading-[1.1] font-extrabold mt-0 mb-[12px]">
              Оптимизация трудовых ресурсов
            </h1>
            <p className="text-[18px] text-[#e5e7eb] max-w-[720px]">
              WFM, многопрофильная работа и продуманные графики. Меньше сверхурочных — выше производительность и предсказуемая почасовая оплата.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold text-base hover:text-white"
              >
                <Link href="#calc" className="flex items-center justify-center h-full">
                  Рассчитать сценарий
                </Link>
              </Button>

              <Button
                asChild
                className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B]"
              >
                <Link href="/pricing" className="flex items-center justify-center h-full">
                  Тарифы
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 px-[48px] py-[24px] flex items-center justify-center">
          <div className="max-w-[900px] mx-auto w-full">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-[14px]">
              <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-[10px] text-center shadow-sm">
                <div className="text-[30px] font-extrabold text-[#003B9B]">+10–18%</div>
                <div className="text-[#64748b] text-base">производительность за 60 дней</div>
              </div>
              <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-[10px] text-center shadow-sm">
                <div className="text-[30px] font-extrabold text-[#003B9B]">−20–35%</div>
                <div className="text-[#64748b] text-base">переработки</div>
              </div>
              <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-[10px] text-center shadow-sm">
                <div className="text-[30px] font-extrabold text-[#003B9B]">≤3%</div>
                <div className="text-[#64748b] text-base">неявка после внедрения</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section id="calc" className="py-[64px] bg-[#f6f8ff]">
        <div className="max-w-[1200px] mx-auto px-6">
          <SimpleFTECalculator />
        </div>
      </section>

      <section className="py-[68px]">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-[22px]">
          <Card className="rounded-[16px] p-[40px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold mb-[8px]">Что внедряем</h3>
            <ul className="flex flex-wrap gap-x-[6px] gap-y-[4px] list-none">
              {["WFM‑модель", "Матрица навыков", "Сменные графики", "Процесс отслеживания времени", "Стандарты смен", "KPI‑дашборды"].map(t =>
                <span key={t} className="inline-block bg-[#eef2ff] text-[#1e293b] px-[10px] py-[6px] rounded-full border border-[#e5e7eb] text-[12px]">{t}</span>
              )}
            </ul>
          </Card>
          <Card className="rounded-[16px] p-[40px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold mb-[8px]">Быстрые победы</h3>
            <ul className="flex flex-wrap gap-x-[6px] gap-y-[4px] list-none">
              {["Срез переработок", "Снижение неявки", "Самовыбор смен", "Кросс‑обучение", "Балансировка линий"].map(t =>
                <span key={t} className="inline-block bg-[#eef2ff] text-[#1e293b] px-[10px] py-[6px] rounded-full border border-[#e5e7eb] text-[12px]">{t}</span>
              )}
            </ul>
          </Card>
          <Card className="rounded-[16px] p-[40px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold mb-[8px]">KPI и контроль</h3>
            <ul className="flex flex-wrap gap-x-[6px] gap-y-[4px] list-none">
              {["ЭПЗ", "Неявки", "Переработки", "Себестоимость часа", "Производительность"].map(t =>
                <span key={t} className="inline-block bg-[#eef2ff] text-[#1e293b] px-[10px] py-[6px] rounded-full border border-[#e5e7eb] text-[12px]">{t}</span>
              )}
            </ul>
          </Card>
        </div>
      </section>

      <section className="py-[64px] bg-[#f6f8ff]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-[10px]">
            {[1, 2, 3, 4, 5, 6].map(i =>
              <div key={i} className="bg-white border border-[#e5e7eb] rounded-[12px] p-[14px] text-center">
                <strong className="text-xl text-slate-900">{i}</strong>
                <div className="text-[#64748b] text-sm">{["Бриф & цели", "Диагностика", "Дизайн", "Пилот", "Масштабирование", "KPI‑ревью"][i - 1]}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-[64px]">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-[22px]">
          <Card className="rounded-[16px] p-[20px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold mb-3">Кейс: складская логистика</h3>
            <p className="text-[#64748b] text-base">
              +16 % к выработке, −27 % переаботок, −18 % текучесть за 90 дней. Экономия ФОТ 5,2 млн ₽/кв.
            </p>
          </Card>
          <Card className="rounded-[16px] p-[20px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold mb-3">Готовы обсудить?</h3>
            <p className="text-[#64748b] text-base">Оставьте контакты — пришлём план.</p>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row gap-[20px] mt-[8px] flex-wrap">
                <div className="flex-1">
                  <Label htmlFor="contactName" className="block text-[12px] text-[#64748b] mb-[6px]">Имя</Label>
                  <Input id="contactName" name="name" value={formData.name} onChange={handleInputChange}
                    placeholder="Как к вам обращаться" className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]" />
                </div>
                <div className="flex-1">
                  <Label htmlFor="contactPhone" className="block text-[12px] text-[#64748b] mb-[6px]">Телефон</Label>
                  <Input id="contactPhone" name="phone" value={formData.phone} onChange={handleInputChange}
                    placeholder="+7 (___) ___‑__‑__" className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]" />
                </div>
                <div className="flex-1">
                  <Label htmlFor="contactEmail" className="block text-[12px] text-[#64748b] mb-[6px]">E-mail</Label>
                  <Input id="contactEmail" name="email" value={formData.email} onChange={handleInputChange}
                    placeholder="work@company.com" className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]" />
                </div>
              </div>
              <div className="mt-4">
                <CustomCheckbox name="consent" isChecked={formData.consent} onChange={handleInputChange} link="/privacy" />
              </div>
              <div className="mt-6">
                <button type="submit"
                  className="bg-[#FF7C22] hover:bg-orange-700 text-white rounded-xl font-bold py-3 px-5 transition-all duration-200 ease-in-out transform hover:-translate-y-0.5">
                  Отправить заявку
                </button>
              </div>
            </form>
            {formStatus && <p className={`mt-2 text-sm ${formStatus.startsWith('❌') ? 'text-red-500' : formStatus.startsWith('⚠️') ? 'text-amber-600' : 'text-green-600'}`}>{formStatus}</p>}
          </Card>
        </div>
      </section>
    </main>
  );
}